// AUTO-GENERATED FILE. DO NOT EDIT.

import {
    Pretend,
    IPretendRequest,
    Get,
    Post,
    Put,
    Headers as PretendHeaders
} from 'pretend';

import * as dtos from './dtos';
import { parseApiDates } from './date';

const bind = <T extends Function>(
    _target: Object,
    key: string | symbol,
    { value: fn }: TypedPropertyDescriptor<T>
): TypedPropertyDescriptor<T> | void => {
    let definingProperty = false;
    return {
        configurable: true,
        get() {
            if (definingProperty) {
                return fn!;
            }
            const value = (...args: unknown[]) => {
                const newArgs = args.map((arg) => {
                    if (!arg) {
                        return arg;
                    }
                    if (typeof arg === 'string') {
                        return encodeURIComponent(arg);
                    }
                    try {
                        if (
                            typeof arg !== 'object' ||
                            Object.getPrototypeOf(arg).constructor !== Object
                        ) {
                            return arg;
                        }
                        return JSON.parse(
                            JSON.stringify(arg, (_, value) =>
                                value === null ? undefined : value
                            )
                        );
                    } catch (err) {
                        throw new Error('error while parsing arguments' + err, {
                            cause: err
                        });
                    }
                });

                return fn!.apply(this, newArgs);
            };

            definingProperty = true;
            Object.defineProperty(this, key, {
                value,
                configurable: true,
                writable: true
            });
            definingProperty = false;
            return value as unknown as T;
        }
    };
};

export const PARSE_RESULT_METADATA_KEY = Symbol('parseResultMetadata');
const parseResult = (props: string[]) => {
    return (
        _target: Object,
        _key: string | symbol,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        descriptor: TypedPropertyDescriptor<any>
    ) => {
        const fn = descriptor.value;

        descriptor.value = async function (...args: unknown[]) {
            const result = await fn.apply(this, args);

            const tupleProps = props.map((prop) => prop.split('.'));

            return parseApiDates(result, tupleProps);
        };

        Reflect.defineMetadata(PARSE_RESULT_METADATA_KEY, props, _target, _key);

        return descriptor;
    };
};

export interface ConnectApiAdditionalAgreementController {
    getAdditionalAgreement(
        customerId: number
    ): Promise<dtos.ConnectApiAdditionalAgreementDto>;

    setAdditionalAgreement(customerId: number): Promise<void>;
}

export interface ConnectApiAuthenticationController {
    /**
     * @deprecated
     */
    grantAccess(
        encryptedCustomerId: string,
        query: { hash: string }
    ): Promise<dtos.AuthDto>;

    grantAccessToken(token: string): Promise<dtos.AuthDto>;

    grantAccessUuid(uuid: string): Promise<dtos.AuthDto>;
}

export interface ConnectApiBookingController {
    cancelBooking(
        bookingId: string
    ): Promise<dtos.ConnectApiBookingConfirmationDto>;

    confirmBooking(
        bookingId: string
    ): Promise<dtos.ConnectApiBookingConfirmationDto>;
}

export interface ConnectApiCommMatrixController {
    getCommMatrix(
        customerId: number
    ): Promise<dtos.CustomerCommunicationConfigurationDto[]>;

    getPrivacyUrl(customerId: number): Promise<dtos.WrappedResultString>;

    updateCommMatrix(
        customerId: number,
        dto: dtos.CustomerCommunicationConfigurationDto[]
    ): Promise<dtos.CustomerCommunicationConfigurationDto[]>;

    updateCommMatrixFull(
        customerId: number,
        dto: dtos.CustomerCommunicationConfigurationDto[]
    ): Promise<dtos.CustomerCommunicationConfigurationDto[]>;
}

export interface ConnectApiContractController {
    cancelContract(
        query: { recaptchaToken: string },
        dto: dtos.CancelContractRequestDto
    ): Promise<dtos.CancelContractResponseDto>;

    cancelContractManual(
        query: { recaptchaToken: string },
        dto: dtos.CancelContractManualRequestDto
    ): Promise<dtos.CancelContractManualResponseDto>;

    getActiveContracts(
        query: { recaptchaToken: string },
        dto: dtos.GetActiveContractsRequestDto
    ): Promise<dtos.GetActiveContractsResponseDto[]>;

    getCancellationReasons(
        studioId: number
    ): Promise<dtos.CancelationReasonDto[]>;

    getStudioInfo(
        studioId: number
    ): Promise<dtos.ConnectApiContractCancellationStudioDto>;

    getWithdrawableContracts(
        query: { recaptchaToken: string },
        dto: dtos.GetWithdrawableContractsRequestDto
    ): Promise<dtos.GetWithdrawableContractsResponseDto[]>;

    withdrawContract(
        query: { recaptchaToken: string },
        dto: dtos.WithdrawContractRequestDto
    ): Promise<dtos.WithdrawContractResponseDto>;

    withdrawContractManual(
        query: { recaptchaToken: string },
        dto: dtos.WithdrawContractManualRequestDto
    ): Promise<dtos.WithdrawContractManualResponseDto>;
}

export interface ConnectApiContractControllerV2 {
    getActiveStudios(query: {
        purpose?: 'CANCELLATION' | 'WITHDRAWAL';
    }): Promise<dtos.ConnectApiBasicStudioDataDtoV2[]>;
}

export interface ConnectApiDoubleOptInController {
    acceptDoubleOptInRequest(
        customerId: number,
        query: { recaptchaToken: string; doubleOptInRequestUuid: string }
    ): Promise<void>;

    declineDoubleOptInRequest(
        customerId: number,
        query: { recaptchaToken: string; doubleOptInRequestUuid: string }
    ): Promise<void>;

    getDoubleOptInChannels(
        customerId: number,
        query: { doubleOptInRequestUuid: string }
    ): Promise<dtos.ConnectApiDoubleOptInChannelsDto>;
}

export interface ConnectApiEmailVerificationController {
    approveEmail(uuid: string): Promise<dtos.ConnectApiEmailVerificationDto>;

    rejectEmail(uuid: string): Promise<dtos.ConnectApiEmailVerificationDto>;
}

export interface ConnectApiPromotionController {
    confirmCustomerParticipation(query: { payload: string }): Promise<void>;
}

export interface ConnectApiRateBundleController {
    createCustomerAndContract(
        dto: dtos.ConnectApiCreateContractDto
    ): Promise<dtos.ConnectApiCreateContractResponseDto>;

    getRateBundleList(query: {
        studioId: number;
    }): Promise<dtos.ConnectApiRateBundleDto[]>;
}

export interface ConnectApiTenantController {
    getTenantInfo(): Promise<dtos.TenantDto>;
}

export interface PublicStudioController {
    getStudioCountry(studioId: number): Promise<dtos.ConnectApiPublicStudioDto>;
}

export interface StudioController {
    getDefaultCommunicationConfiguration(
        studioId: number
    ): Promise<dtos.ConnectApiStudioCommunicationConfigurationDto[]>;

    getSepaAgreementText(studioId: number): Promise<string>;

    getSepaAgreementTextForRateBundle(
        studioId: number,
        rateBundleId: number,
        query: { language?: string }
    ): Promise<string>;

    getUtilizationPublic(studioId: number): Promise<dtos.UtilizationDto>;
}

export interface StudioControllerV2 {
    getEnabledCommunicationFeatureModules(
        studioId: number
    ): Promise<
        (
            | 'ANALYTICS'
            | 'CALENDAR'
            | 'CASH_REGISTER'
            | 'CHECK_IN'
            | 'CHECK_IN_BENEFITS'
            | 'CHECK_IN_DISCOUNT'
            | 'CLASSES'
            | 'CORPORATE_FITNESS'
            | 'CROSS_LOCATION_SALES'
            | 'CUSTOMER_GROUPS'
            | 'CUSTOMER_RECOVERY'
            | 'EPOST'
            | 'EQUIPMENT'
            | 'HARDWARE'
            | 'INVENTORY_MANAGEMENT'
            | 'LEAD_MANAGEMENT'
            | 'LETTER'
            | 'LOCATIONS'
            | 'LOYALTY_PROGRAM'
            | 'MAGICLINE_WEBSITES'
            | 'MEMBER_APP'
            | 'MEMBER_CARE_SYSTEM'
            | 'MEMBER_RETENTION_HUB'
            | 'MEMBERS_RECRUIT_MEMBERS'
            | 'MY_SPORTS_MESSAGES'
            | 'RETURNS'
            | 'SALES_TOOL'
            | 'SECOND_SCREEN'
            | 'SERVICES'
            | 'SHIFT_PLANNING'
            | 'STUDIO_NEWS'
            | 'TASK_MANAGEMENT'
            | 'TEXT_MESSAGE'
            | 'TIME_TRACKING'
            | 'TOUCHPOINT'
            | 'TRAINING_WORLD'
            | 'TRIAL_TRAINING'
            | 'VOUCHERS'
            | 'TEST_MODULE_FOR_REMOVAL'
            | 'TEST_MODULE_1'
            | 'TEST_MODULE_2'
            | 'TEST_MODULE_3'
            | 'TEST_MODULE_4'
            | 'TEST_MODULE_5'
            | 'TEST_MODULE_6'
            | 'TEST_MODULE_7'
            | 'TEST_MODULE_8'
            | 'TEST_MODULE_9'
            | 'TEST_MODULE_10'
            | 'TEST_MODULE_11'
        )[]
    >;

    getStudio(studioId: number): Promise<dtos.ConnectApiStudioDtoV2>;

    getStudioDetails(
        studioId: number
    ): Promise<dtos.ConnectApiStudioDetailsDto>;

    getStudioLegalLinks(
        studioId: number
    ): Promise<dtos.ConnectApiStudioLegalLinksDto>;

    getStudioList(query: {
        studioTags?: string[];
        addressSearchQuery?: string;
    }): Promise<dtos.ConnectApiStudioDtoV2[]>;
}

export interface PublicContentPigeon {
    ConnectApiAdditionalAgreementController: ConnectApiAdditionalAgreementController;
    ConnectApiAuthenticationController: ConnectApiAuthenticationController;
    ConnectApiBookingController: ConnectApiBookingController;
    ConnectApiCommMatrixController: ConnectApiCommMatrixController;
    ConnectApiContractController: ConnectApiContractController;
    ConnectApiContractControllerV2: ConnectApiContractControllerV2;
    ConnectApiDoubleOptInController: ConnectApiDoubleOptInController;
    ConnectApiEmailVerificationController: ConnectApiEmailVerificationController;
    ConnectApiPromotionController: ConnectApiPromotionController;
    ConnectApiRateBundleController: ConnectApiRateBundleController;
    ConnectApiTenantController: ConnectApiTenantController;
    PublicStudioController: PublicStudioController;
    StudioController: StudioController;
    StudioControllerV2: StudioControllerV2;
}

/* eslint-disable @typescript-eslint/no-explicit-any */

class ConnectApiAdditionalAgreementControllerBlueprint
    implements ConnectApiAdditionalAgreementController
{
    @bind
    @parseResult(['agreementTime'])
    @Get('/connect/secured/agreement-setting/{customerId}')
    public getAdditionalAgreement(_p0: any): any {
        /**/
    }

    @bind
    @Post('/connect/secured/agreement-setting/{customerId}')
    public setAdditionalAgreement(_p0: any): any {
        /**/
    }
}

class ConnectApiAuthenticationControllerBlueprint
    implements ConnectApiAuthenticationController
{
    @bind
    @Get('/connect/auth/grant-for-customer/{encryptedCustomerId}', true)
    public grantAccess(_p0: any, _p1: any): any {
        /**/
    }

    @bind
    @Get('/connect/auth/grant-for-customer-by-token/{token}')
    public grantAccessToken(_p0: any): any {
        /**/
    }

    @bind
    @Get('/connect/auth/grant-for-customer-by/{uuid}')
    public grantAccessUuid(_p0: any): any {
        /**/
    }
}

class ConnectApiBookingControllerBlueprint
    implements ConnectApiBookingController
{
    @bind
    @parseResult([
        'endDateTime',
        'startDateTime',
        'studio.closingDate',
        'studio.openingDate'
    ])
    @Post('/connect/v1/booking/{bookingId}/cancel')
    public cancelBooking(_p0: any): any {
        /**/
    }

    @bind
    @parseResult([
        'endDateTime',
        'startDateTime',
        'studio.closingDate',
        'studio.openingDate'
    ])
    @Post('/connect/v1/booking/{bookingId}/confirm')
    public confirmBooking(_p0: any): any {
        /**/
    }
}

class ConnectApiCommMatrixControllerBlueprint
    implements ConnectApiCommMatrixController
{
    @bind
    @Get('/connect/secured/comm-setting/{customerId}')
    public getCommMatrix(_p0: any): any {
        /**/
    }

    @bind
    @Get('/connect/secured/comm-setting/{customerId}/privacy-url')
    public getPrivacyUrl(_p0: any): any {
        /**/
    }

    @bind
    @PretendHeaders('Content-Type: application/json')
    @Put('/connect/secured/comm-setting/{customerId}')
    public updateCommMatrix(_p0: any, _p1: any): any {
        /**/
    }

    @bind
    @PretendHeaders('Content-Type: application/json')
    @Put('/connect/secured/comm-setting/{customerId}/full')
    public updateCommMatrixFull(_p0: any, _p1: any): any {
        /**/
    }
}

class ConnectApiContractControllerBlueprint
    implements ConnectApiContractController
{
    @bind
    @PretendHeaders('Content-Type: application/json')
    @parseResult([
        'cancellationDate',
        'cancellationReceivedDateTime',
        'dateOfBirth'
    ])
    @Post('/connect/v1/contracts/cancel', true)
    public cancelContract(_p0: any, _p1: any): any {
        /**/
    }

    @bind
    @PretendHeaders('Content-Type: application/json')
    @parseResult(['cancellationDate', 'dateOfBirth', 'requestReceivedDate'])
    @Post('/connect/v1/contracts/cancel-request', true)
    public cancelContractManual(_p0: any, _p1: any): any {
        /**/
    }

    @bind
    @PretendHeaders('Content-Type: application/json')
    @parseResult(['cancellationDates'])
    @Post('/connect/v1/contracts', true)
    public getActiveContracts(_p0: any, _p1: any): any {
        /**/
    }

    @bind
    @Get('/connect/v1/contracts/studios/{studioId}/cancellation-reasons')
    public getCancellationReasons(_p0: any): any {
        /**/
    }

    @bind
    @Get('/connect/v1/contracts/studios/{studioId}')
    public getStudioInfo(_p0: any): any {
        /**/
    }

    @bind
    @PretendHeaders('Content-Type: application/json')
    @Post('/connect/v1/contracts/withdrawable', true)
    public getWithdrawableContracts(_p0: any, _p1: any): any {
        /**/
    }

    @bind
    @PretendHeaders('Content-Type: application/json')
    @parseResult(['dateOfBirth', 'withdrawalReceivedDateTime'])
    @Post('/connect/v1/contracts/withdraw', true)
    public withdrawContract(_p0: any, _p1: any): any {
        /**/
    }

    @bind
    @PretendHeaders('Content-Type: application/json')
    @parseResult(['dateOfBirth', 'requestReceivedDate'])
    @Post('/connect/v1/contracts/withdraw-request', true)
    public withdrawContractManual(_p0: any, _p1: any): any {
        /**/
    }
}

class ConnectApiContractControllerV2Blueprint
    implements ConnectApiContractControllerV2
{
    @bind
    @Get('/connect/v2/contracts/studios', true)
    public getActiveStudios(_p0: any): any {
        /**/
    }
}

class ConnectApiDoubleOptInControllerBlueprint
    implements ConnectApiDoubleOptInController
{
    @bind
    @Put('/connect/secured/doi/{customerId}/accept', true)
    public acceptDoubleOptInRequest(_p0: any, _p1: any): any {
        /**/
    }

    @bind
    @Put('/connect/secured/doi/{customerId}/decline', true)
    public declineDoubleOptInRequest(_p0: any, _p1: any): any {
        /**/
    }

    @bind
    @Get('/connect/secured/doi/{customerId}', true)
    public getDoubleOptInChannels(_p0: any, _p1: any): any {
        /**/
    }
}

class ConnectApiEmailVerificationControllerBlueprint
    implements ConnectApiEmailVerificationController
{
    @bind
    @Post('/connect/v1/email-verification/{uuid}/approve')
    public approveEmail(_p0: any): any {
        /**/
    }

    @bind
    @Post('/connect/v1/email-verification/{uuid}/reject')
    public rejectEmail(_p0: any): any {
        /**/
    }
}

class ConnectApiPromotionControllerBlueprint
    implements ConnectApiPromotionController
{
    @bind
    @Post('/connect/v1/promotion/confirm-participation', true)
    public confirmCustomerParticipation(_p0: any): any {
        /**/
    }
}

class ConnectApiRateBundleControllerBlueprint
    implements ConnectApiRateBundleController
{
    @bind
    @PretendHeaders('Content-Type: application/json')
    @Post('/connect/v1/rate-bundle')
    public createCustomerAndContract(_p0: any): any {
        /**/
    }

    @bind
    @parseResult([
        'limitedOfferingPeriod.endDate',
        'limitedOfferingPeriod.startDate',
        'terms.contractStartDateOfUse',
        'terms.defaultContractStartDate',
        'terms.subsequentRateDto.limitedOfferingPeriod.endDate',
        'terms.subsequentRateDto.limitedOfferingPeriod.startDate',
        'terms.subsequentRateDto.terms.contractStartDateOfUse',
        'terms.subsequentRateDto.terms.defaultContractStartDate',
        'terms.subsequentRateDto.terms.subsequentRateDto.limitedOfferingPeriod.endDate',
        'terms.subsequentRateDto.terms.subsequentRateDto.limitedOfferingPeriod.startDate',
        'terms.subsequentRateDto.terms.subsequentRateDto.terms.contractStartDateOfUse',
        'terms.subsequentRateDto.terms.subsequentRateDto.terms.defaultContractStartDate'
    ])
    @Get('/connect/v1/rate-bundle', true)
    public getRateBundleList(_p0: any): any {
        /**/
    }
}

class ConnectApiTenantControllerBlueprint
    implements ConnectApiTenantController
{
    @bind
    @Get('/connect/v1/tenant')
    public getTenantInfo(): any {
        /**/
    }
}

class PublicStudioControllerBlueprint implements PublicStudioController {
    @bind
    @Get('/connect/v2/public/studio/{studioId}')
    public getStudioCountry(_p0: any): any {
        /**/
    }
}

class StudioControllerBlueprint implements StudioController {
    @bind
    @Get('/connect/v1/studio/{studioId}/communication-settings')
    public getDefaultCommunicationConfiguration(_p0: any): any {
        /**/
    }

    @bind
    @Get('/connect/v1/studio/{studioId}/sepa/agreement')
    public getSepaAgreementText(_p0: any): any {
        /**/
    }

    @bind
    @Get(
        '/connect/v1/studio/{studioId}/rate/{rateBundleId}/sepa/agreement',
        true
    )
    public getSepaAgreementTextForRateBundle(
        _p0: any,
        _p1: any,
        _p2: any
    ): any {
        /**/
    }

    @bind
    @Get('/connect/v1/studio/{studioId}/utilization')
    public getUtilizationPublic(_p0: any): any {
        /**/
    }
}

class StudioControllerV2Blueprint implements StudioControllerV2 {
    @bind
    @Get('/connect/v2/studio/{studioId}/communicationFeatures')
    public getEnabledCommunicationFeatureModules(_p0: any): any {
        /**/
    }

    @bind
    @parseResult(['closingDate', 'openingDate'])
    @Get('/connect/v2/studio/{studioId}')
    public getStudio(_p0: any): any {
        /**/
    }

    @bind
    @parseResult(['closingDate', 'openingDate'])
    @Get('/connect/v2/studio/details/{studioId}')
    public getStudioDetails(_p0: any): any {
        /**/
    }

    @bind
    @Get('/connect/v2/studio/{studioId}/legalinfo')
    public getStudioLegalLinks(_p0: any): any {
        /**/
    }

    @bind
    @parseResult(['closingDate', 'openingDate'])
    @Get('/connect/v2/studio', true)
    public getStudioList(_p0: any): any {
        /**/
    }
}

/* eslint-enable @typescript-eslint/no-explicit-any */

export function getClient(
    endpoint: string,
    token: string | undefined,
    configure: (client: Pretend) => Pretend = (client) => client
): PublicContentPigeon {
    const bearerToken = (request: IPretendRequest) => {
        if (token) {
            request.options.headers = new Headers(request.options.headers);
            request.options.headers.set('Authorization', `bearer ${token}`);
        }
        return request;
    };

    return {
        ConnectApiAdditionalAgreementController: configure(Pretend.builder())
            .requestInterceptor(bearerToken)
            .target(ConnectApiAdditionalAgreementControllerBlueprint, endpoint),
        ConnectApiAuthenticationController: configure(Pretend.builder())
            .requestInterceptor(bearerToken)
            .target(ConnectApiAuthenticationControllerBlueprint, endpoint),
        ConnectApiBookingController: configure(Pretend.builder())
            .requestInterceptor(bearerToken)
            .target(ConnectApiBookingControllerBlueprint, endpoint),
        ConnectApiCommMatrixController: configure(Pretend.builder())
            .requestInterceptor(bearerToken)
            .target(ConnectApiCommMatrixControllerBlueprint, endpoint),
        ConnectApiContractController: configure(Pretend.builder())
            .requestInterceptor(bearerToken)
            .target(ConnectApiContractControllerBlueprint, endpoint),
        ConnectApiContractControllerV2: configure(Pretend.builder())
            .requestInterceptor(bearerToken)
            .target(ConnectApiContractControllerV2Blueprint, endpoint),
        ConnectApiDoubleOptInController: configure(Pretend.builder())
            .requestInterceptor(bearerToken)
            .target(ConnectApiDoubleOptInControllerBlueprint, endpoint),
        ConnectApiEmailVerificationController: configure(Pretend.builder())
            .requestInterceptor(bearerToken)
            .target(ConnectApiEmailVerificationControllerBlueprint, endpoint),
        ConnectApiPromotionController: configure(Pretend.builder())
            .requestInterceptor(bearerToken)
            .target(ConnectApiPromotionControllerBlueprint, endpoint),
        ConnectApiRateBundleController: configure(Pretend.builder())
            .requestInterceptor(bearerToken)
            .target(ConnectApiRateBundleControllerBlueprint, endpoint),
        ConnectApiTenantController: configure(Pretend.builder())
            .requestInterceptor(bearerToken)
            .target(ConnectApiTenantControllerBlueprint, endpoint),
        PublicStudioController: configure(Pretend.builder())
            .requestInterceptor(bearerToken)
            .target(PublicStudioControllerBlueprint, endpoint),
        StudioController: configure(Pretend.builder())
            .requestInterceptor(bearerToken)
            .target(StudioControllerBlueprint, endpoint),
        StudioControllerV2: configure(Pretend.builder())
            .requestInterceptor(bearerToken)
            .target(StudioControllerV2Blueprint, endpoint)
    };
}
